import { SubscriptionPricingInputs } from './SubscriptionPricingInputs';
import React, { useEffect, useState } from 'react';
import {
  BooleanInput,
  FormWithRedirect,
  NumberInput,
  minValue,
  maxValue,
  required,
  SaveButton,
  useDataProvider,
  useNotify,
  useUpdate,
} from 'react-admin';
import { useHistory } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { useTranslate } from '../common/language';
import { targets } from '../common/constants';
import { BoxPhotoUser } from '../resources/docs';
import { validateInteger } from './Validator';

const valueOrDefault = (value, defaultValue) =>
  value === null || value === undefined ? defaultValue : value;

export const SettingsTicketsGeneral = () => {
  const translate = useTranslate();
  const history = useHistory();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [update] = useUpdate();
  const [info, setInfo] = useState();
  const associationId = localStorage.getItem('associations_id');
  const integerValidation = [required(), validateInteger(translate('common.validation.number'))];

  useEffect(() => {
    if (associationId > 0) {
      dataProvider.getOne('associations', { id: associationId })
        .then(({ data }) => setInfo(data))
        .catch(error => notify(error.message, 'error'));
    }
  }, [associationId, dataProvider, notify]);

  if (!info) return null;

  const handleSubmit = values => {
    update(
      {
        type: 'update',
        resource: 'associations/tickets/general',
        payload: {
          id: values.id,
          data: {
            tickets_validators_start_minutes: values.tickets_validators_start_minutes,
            tickets_validators_finish_minutes: values.tickets_validators_finish_minutes,
            tickets_open_doors_minutes: values.tickets_open_doors_minutes,
            tickets_max_per_purchase: values.tickets_max_per_purchase,
            tickets_sale_enabled: values.tickets_sale_enabled,
            tickets_vat_percent: values.tickets_vat_percent,
            subscriptions_vat_percent: values.subscriptions_vat_percent,
            subscriptions_vat_included: values.subscriptions_vat_included,
            subscriptions_management_fees_included: values.subscriptions_management_fees_included,
            tickets_vat_included: values.tickets_vat_included,
            tickets_management_fees_included: values.tickets_management_fees_included,
            tickets_sale_start_days: values.tickets_sale_start_days,
            tickets_sale_finish_days: values.tickets_sale_finish_days,
            tickets_box_office_start_days: values.tickets_box_office_start_days,
            tickets_box_office_finish_days: values.tickets_box_office_finish_days,
          },
        },
      },
      {
        onSuccess: () => history.push('/settings-tickets'),
        onFailure: error => notify(error.message, 'error'),
      }
    );
  };

  return (
    <FormWithRedirect
      resource="associations"
      save={handleSubmit}
      initialValues={{
        id: associationId,
        tickets_validators_start_minutes: valueOrDefault(info.tickets_validators_start_minutes, 180),
        tickets_validators_finish_minutes: valueOrDefault(info.tickets_validators_finish_minutes, 0),
        tickets_open_doors_minutes: valueOrDefault(info.tickets_open_doors_minutes, 30),
        tickets_max_per_purchase: valueOrDefault(info.tickets_max_per_purchase, 10),
        tickets_sale_enabled: info.tickets_sale_enabled !== false,
        tickets_vat_percent: valueOrDefault(info.tickets_vat_percent, 10),
        subscriptions_vat_percent: valueOrDefault(info.subscriptions_vat_percent, 10),
        subscriptions_vat_included: valueOrDefault(info.subscriptions_vat_included, true),
        subscriptions_management_fees_included: valueOrDefault(info.subscriptions_management_fees_included, false),
        tickets_vat_included: valueOrDefault(info.tickets_vat_included, true),
        tickets_management_fees_included: valueOrDefault(info.tickets_management_fees_included, false),
        tickets_sale_start_days: valueOrDefault(info.tickets_sale_start_days, 30),
        tickets_sale_finish_days: valueOrDefault(info.tickets_sale_finish_days, 0),
        tickets_box_office_start_days: valueOrDefault(info.tickets_box_office_start_days, 30),
        tickets_box_office_finish_days: valueOrDefault(info.tickets_box_office_finish_days, 0),
      }}
      render={formProps => (
        <>
          <br />
          <Typography variant="h5" className="grey8" align="center">
            {translate('tickets_general.title')}
          </Typography>
          <Box sx={{ width: '100%', maxWidth: 850, bgcolor: 'background.paper', padding: '25px' }} className="settings_box">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <BooleanInput fullWidth source="tickets_sale_enabled" label={translate('tickets_general.enabled')} />
              </Grid>

              <Grid item xs={12}>
                <Box
                  style={{
                    minWidth: 0,
                    border: '1px solid #e0e0e0',
                    borderRadius: 4,
                    padding: 16,
                    backgroundColor: '#fff'
                  }}
                >
                  <Typography variant="subtitle1" style={{ marginBottom: 12, fontWeight: 600 }}>
                    {translate('tickets_general.ticket_header_logo')}
                  </Typography>
                  <Box className="ara-tiquets-config-logo">
                    <BoxPhotoUser
                      target={targets.TICKETS_TICKET_HEADER_LOGO}
                      items_id={associationId}
                      edit
                      quantity={1}
                      className="ara-tiquets-config-logo-image"
                    />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6">{translate('tickets_general.online_sale_period')}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <NumberInput fullWidth source="tickets_sale_start_days" label={translate('tickets_general.start_days')} validate={integerValidation} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <NumberInput fullWidth source="tickets_sale_finish_days" label={translate('tickets_general.finish_days')} validate={integerValidation} />
              </Grid>

              <Grid item xs={12} sm={4}>
                <NumberInput fullWidth source="tickets_max_per_purchase" label={translate('tickets_general.tickets_max_per_purchase')} step={1} validate={[...integerValidation, minValue(1)]} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6">{translate('tickets_general.box_office_sale_period')}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <NumberInput fullWidth source="tickets_box_office_start_days" label={translate('tickets_general.start_days')} validate={integerValidation} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <NumberInput fullWidth source="tickets_box_office_finish_days" label={translate('tickets_general.finish_days')} validate={integerValidation} />
              </Grid>

            </Grid>

            <Box mt={3} mb={2}>
              <Typography variant="h6">{translate('tickets_general.event_access')}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <NumberInput fullWidth source="tickets_validators_start_minutes" label={translate('tickets_general.tickets_validators_start_minutes')} step={1} validate={[...integerValidation, minValue(0)]} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <NumberInput fullWidth source="tickets_validators_finish_minutes" label={translate('tickets_general.tickets_validators_finish_minutes')} step={1} validate={[...integerValidation, minValue(0)]} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <NumberInput fullWidth source="tickets_open_doors_minutes" label={translate('tickets_general.tickets_open_doors_minutes')} step={1} validate={[...integerValidation, minValue(0)]} />
              </Grid>
            </Grid>
            </Box>

            <Box style={{ border: '1px solid #ddd', borderRadius: 4, padding: 16, margin: '16px 0' }}>
              <Typography variant="h6" gutterBottom>{translate('settings.service_block_tickets')}</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <NumberInput fullWidth source="tickets_vat_percent" label={translate('settings.tickets_vat_percent_default')} step={0.01} validate={[required(), minValue(0), maxValue(100)]} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <BooleanInput fullWidth source="tickets_vat_included" label={translate('settings.tickets_vat_included_default')} />
                </Grid>
                <Grid item xs={12}>
                  <BooleanInput fullWidth source="tickets_management_fees_included" label={translate('settings.tickets_management_fees_included')} />
                </Grid>
              </Grid>
            </Box>
            <SubscriptionPricingInputs />
            <SaveButton saving={formProps.saving} handleSubmitWithRedirect={formProps.handleSubmitWithRedirect} />
          </Box>
        </>
      )}
    />
  );
};
